<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import WizardSummary from '../components/WizardSummary.vue';
import CustomSelect from '../../shared/components/CustomSelect.vue';
import {
  findCompaniesGPS,
  findZipCode,
  createOrUpdateClient,
} from '../../../../repositories/landing.repository';
import { evaluateNames } from '../../../../helpers';
import storePolicy from '@store/policy';

const props = defineProps<{ clientUpdates: any[] }>();
const emit = defineEmits<{ (e: 'next'): void; (e: 'back'): void }>();

const { t } = useI18n();
const store = storePolicy();

const tradeName = ref<string>('');
const email = ref<string>(store.data?.email ?? '');
const phone1 = ref<string>('');
const companyGPSId = ref<any>(null);
const address = ref<string>('');
const postCode = ref<string>('');
const colonyId = ref<any>(null);
const city = ref<string>('');
const state = ref<string>('');

const companyOptions = ref<any[]>([]);
const colonyOptions = ref<any[]>([]);
const companyLocked = ref<boolean>(false);
const error = ref<string>('');
const loading = ref<boolean>(false);

// Precarga desde updatesClientInputs (lookup del RFC en "Tus datos")
const applyClientUpdates = (updates: any[]) => {
  for (const u of updates) {
    switch (u.key) {
      case 'tradeName':
        tradeName.value = u.value ?? '';
        break;
      case 'phone1':
        phone1.value = u.value ?? '';
        break;
      case 'companyGPSId':
        companyGPSId.value = u.value?.id ?? null;
        break;
      case 'address':
        address.value = u.value ?? '';
        break;
      case 'postCode':
        postCode.value = u.value ?? '';
        break;
      case 'colony':
        if (u.options) colonyOptions.value = u.options;
        colonyId.value = u.value?.id ?? null;
        break;
      case 'city':
        city.value = u.value ?? '';
        break;
      case 'state':
        state.value = u.value ?? '';
        break;
    }
  }
};

onMounted(async () => {
  companyOptions.value = (await findCompaniesGPS()) as any[];

  if (props.clientUpdates?.length) {
    applyClientUpdates(props.clientUpdates);
  }

  // si el usuario ya editó datos (fue y volvió), los cambios guardados
  // en store.client pisan la precarga del RFC
  const saved = store.client ?? {};
  if (saved.tradeName) tradeName.value = saved.tradeName;
  if (saved.email) email.value = saved.email;
  if (saved.phone1) phone1.value = saved.phone1;
  if (saved.address) address.value = saved.address;
  if (saved.postCode) postCode.value = saved.postCode;
  if (saved.companyGPS?.id) companyGPSId.value = saved.companyGPS.id;

  // si hay CP pero no llegaron opciones de colonia, las buscamos
  if (postCode.value && !colonyOptions.value.length) {
    await lookupPostCode();
  }
  if (saved.neighborhood?.id) {
    const exists = colonyOptions.value.find(
      (c: any) => c.id === saved.neighborhood.id,
    );
    if (exists) colonyId.value = saved.neighborhood.id;
  }

  // preselección por parámetro ?gpsCompany=slug (queda bloqueada)
  const gpsCompany = store.data?.queryParams?.gpsCompany;
  if (gpsCompany) {
    const company = companyOptions.value.find(
      (c: any) => c.slug === gpsCompany,
    );
    if (company) {
      companyGPSId.value = company.id;
      companyLocked.value = true;
    }
  }
});

// Busca colonias y completa ciudad/estado a partir del CP (misma lógica que FormClientMX)
const lookupPostCode = async () => {
  const location = await findZipCode(postCode.value as any, false);
  if (!location?.data?.length) return;

  colonyOptions.value = location.data.map((loc: any) => ({
    nombre: loc.ColoniaDsc,
    id: loc.id,
  }));
  state.value = location.data[0].EstadoNombre;
  city.value = location.data[0].MunicipioNombre;
  postCode.value = location.data[0].code;

  store.setProp({
    key: 'client',
    value: {
      ...store.client,
      country: { id: location.data[0].PaisID },
      city: { id: location.data[0].MunicipioId },
      neighborhood: { id: location.data[0].ColoniaId },
      postCode: location.data[0].code,
      rfc: store.client.rfc,
      state: { id: location.data[0].EstadoID },
    },
  });
};

// Escucha el tipeo del CP y dispara la búsqueda cuando el largo es válido
const handlePostCodeInput = async () => {
  if (!postCode.value || postCode.value.length < 4 || postCode.value.length > 7)
    return;
  colonyId.value = null;
  await lookupPostCode();
};

const handleColonyChange = (id: any) => {
  store.setProp({
    key: 'client',
    value: { ...store.client, neighborhood: { id } },
  });
};

// Fusiona los valores actuales del formulario en store.client
// (se usa al continuar y también al volver, para no perder cambios)
const persistFields = () => {
  const company = companyOptions.value.find(
    (c: any) => c.id === companyGPSId.value,
  );

  store.setProp({
    key: 'client',
    value: {
      ...(store.client ?? {}),
      tradeName: tradeName.value.toUpperCase(),
      email: email.value,
      phone1: phone1.value,
      address: address.value,
      postCode: postCode.value,
      companyGPS: {
        id: companyGPSId.value,
        description: company?.nombre ?? store.client?.companyGPS?.description,
      },
    },
  });
};

const handleBack = () => {
  persistFields();
  emit('back');
};

const handleContinue = async () => {
  error.value = '';

  const filled =
    tradeName.value &&
    email.value &&
    phone1.value &&
    companyGPSId.value &&
    address.value &&
    postCode.value &&
    colonyId.value &&
    city.value &&
    state.value;

  if (!filled) {
    error.value = t('landing.wizard.revisa_datos.error');
    return;
  }

  persistFields();
  const client = store.client ?? {};

  // Validación RFC + nombre (misma que el flujo original)
  const {
    clientMotherSurname,
    clientFathersSurname,
    clientNames,
    clientBirthOrConstDate,
    taxIdVerificationCode,
    namesError,
    isClientAPhysicalPerson,
  } = evaluateNames(client.rfc, client.tradeName);

  if (namesError) {
    error.value = t(
      'landing.terrestre.contratacion_mx.ingrese_su_nombre_completo_seguido_de',
    );
    return;
  }

  try {
    loading.value = true;

    const createOrUpdateClientData = {
      isClientAPhysicalPerson,
      clientMotherSurname,
      clientFathersSurname,
      clientNames,
      clientBirthOrConstDate,
      taxIdVerificationCode,
      namesError,
      address: client.address,
      email: client.email,
      tradeName: client.tradeName,
      phone1: client.phone1,
      countryId: client.country.id,
      cityId: client.city.id,
      neighborhoodId: client.neighborhood.id,
      postCode: client.postCode,
      rfc: client.rfc,
      stateId: client.state.id,
      companyGPSId: client?.companyGPS?.id,
      urlDomain: window.location.origin,
    };

    const createdClient = await createOrUpdateClient(
      client.id,
      createOrUpdateClientData,
      'MX',
    );

    store.setProp({
      key: 'client',
      value: {
        ...client,
        id: createdClient.data.client.id,
        managerId: createdClient.data.manager.id,
      },
    });

    emit('next');
  } catch (e: any) {
    error.value = e?.message ?? t('global.ocurrio_un_error');
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <section class="wz-screen">
    <div class="wz-grid wz-grid--datos">
      <WizardSummary />

      <aside class="wz-card">
        <h2>{{ t('landing.wizard.revisa_datos.titulo_card') }}</h2>
        <p class="sub">{{ t('landing.wizard.revisa_datos.subtitulo_card') }}</p>

        <div class="wz-field">
          <label for="wz-tradeName">{{
            t('landing.forms.form_client_mx.nombre_del_cliente')
          }}</label>
          <input
            id="wz-tradeName"
            v-model="tradeName"
            type="text"
            style="text-transform: uppercase"
          />
        </div>

        <div class="wz-field">
          <label for="wz-email2">{{
            t('landing.forms.form_client_mx.email_del_cliente')
          }}</label>
          <input id="wz-email2" v-model="email" type="email" />
        </div>

        <div class="wz-field">
          <label for="wz-phone1">{{
            t('landing.forms.form_client_mx.telefono_del_cliente')
          }}</label>
          <input id="wz-phone1" v-model="phone1" type="tel" />
        </div>

        <div class="wz-field">
          <label>{{ t('landing.forms.form_client_mx.empresa_gps') }}</label>
          <CustomSelect
            v-model="companyGPSId"
            :options="companyOptions"
            label-key="nombre"
            value-key="id"
            :disabled="companyLocked"
            :placeholder="t('landing.wizard.cotiza.elige_opcion')"
          />
        </div>

        <div class="wz-field">
          <label for="wz-address">{{
            t('landing.forms.form_client_mx.direccion_del_cliente')
          }}</label>
          <input id="wz-address" v-model="address" type="text" />
        </div>

        <div class="wz-field">
          <label for="wz-postCode">{{
            t('landing.forms.form_client_mx.codigo_postal')
          }}</label>
          <input
            id="wz-postCode"
            v-model="postCode"
            type="text"
            :placeholder="t('landing.forms.form_client_mx.codigo_postal_placeholder')"
            @input="handlePostCodeInput"
          />
        </div>

        <div class="wz-field">
          <label>{{ t('landing.forms.form_client_mx.colonia') }}</label>
          <CustomSelect
            v-model="colonyId"
            :options="colonyOptions"
            label-key="nombre"
            value-key="id"
            :placeholder="t('landing.wizard.cotiza.elige_opcion')"
            @change="handleColonyChange"
          />
        </div>

        <div class="wz-field">
          <label for="wz-city">{{
            t('landing.forms.form_client_mx.ciudad')
          }}</label>
          <input id="wz-city" v-model="city" type="text" disabled />
        </div>

        <div class="wz-field">
          <label for="wz-state">{{
            t('landing.forms.form_client_mx.estado')
          }}</label>
          <input id="wz-state" v-model="state" type="text" disabled />
        </div>

        <p v-if="error" class="wz-error">{{ error }}</p>

        <button
          type="button"
          class="wz-cta blue"
          :disabled="loading"
          @click="handleContinue"
        >
          {{
            loading
              ? t('landing.wizard.tus_datos.comprobando')
              : t('landing.wizard.revisa_datos.continuar')
          }}
        </button>
        <button type="button" class="wz-back" @click="handleBack">
          {{ t('landing.wizard.revisa_datos.volver') }}
        </button>
      </aside>
    </div>
  </section>
</template>
